'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import PrescriptionTable from '@/components/PrescriptionTable';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import { getApiPrescriptionType } from '@/app/_lens-components/utils';
import { EPrescriptionType } from '@/utils/enums/goods';
import { useStore } from '@/providers/app-store-provider';
import { font } from '@/theme/zeelool.theme';

export interface PrescriptionConfirmDialogProps {
  open: boolean;
  prescriptionOptions: IPrescriptionOptions;
  prescriptionType: EPrescriptionType;
  onClose: () => void;
  onConfirm: () => void;
  onEdit: () => void;
}

const PrescriptionConfirmDialog: React.FC<PrescriptionConfirmDialogProps> = ({
  open,
  prescriptionOptions,
  prescriptionType,
  onClose,
  onConfirm,
  onEdit,
}) => {
  const t = useTranslations('lens-process');
  const userInfo = useStore((state) => state.userInfo);

  const toTerms = () => {
    window.open('/terms-conditions', '_blank');
  };

  const confirmPresContent = () => {
    return (
      <>
        {/* <div className="msg-box p-[8px_9px] xl:p-[10px] bg-[#fff9d7] rounded-[6px] mb-[6px] xl:mb-[14px]">
          <div className="msg-item text-[12px] xl:text-[14px]">{t('sphPlusTip')}</div>
        </div> */}
        <PrescriptionTable
          prescription={{
            prescription: prescriptionOptions,
            prescription_type: getApiPrescriptionType(prescriptionType),
          }}
          isReadOnly
          onDelete={() => {}}
          onEdit={() => {}}
          onUploadImg={() => {}}
          showGray
          showError
          showPrism
        />
        {userInfo?.is_agreement == 2 && (
          <p className="agreement text-[12px] xl:text-[14px] mt-[14px]">
            {t('byClickingConfirmYouAgreeToOur')}{' '}
            <span className="underline cursor-pointer text-primary-purple" onClick={toTerms}>
              {t('termsAndConditions')}
            </span>
            .{' '}
            {t(
              'iCertifyThatTheWearerIsOver16YearsOldAndThatTheyAreNotRegisteredBlindOrPartiallySighted',
            )}
          </p>
        )}
      </>
    );
  };

  return (
    <>
      <WapOnly>
        <Popup
          open={open}
          showDivider={false}
          title={t('doesThisMatchYourPrescription')}
          onClose={onClose}
          bodyClassName="!p-0"
        >
          <div className="relative">
            <div className="px-14 pb-[95px] max-h-[70vh] overflow-y-auto">
              {confirmPresContent()}
            </div>
            <div className="flex absolute bottom-0 z-2 bg-white left-0 w-full p-[15px] gap-[15px] justify-center shadow-[0px_-2px_10px_0px_rgba(204,204,204,0.5)]">
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={onEdit}
                sx={{
                  '&.MuiButton-root': {
                    fontFamily: font.bb,
                  },
                }}
              >
                {t('edit')}
              </Button>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={onConfirm}
                sx={{
                  '&.MuiButton-root': {
                    fontFamily: font.bb,
                  },
                }}
              >
                {t('confirm')}
              </Button>
            </div>
          </div>
        </Popup>
      </WapOnly>
      <WebOnly>
        <Dialog
          translate="no"
          open={open}
          title={t('doesThisMatchYourPrescription')}
          onClose={onClose}
          fullWidth
          maxWidth="lg"
          cancelText={t('edit')}
          sx={{
            '& .MuiDialog-paper': {
              minWidth: '658px',
            },
          }}
          okText={t('confirm')}
          onOk={onConfirm}
          onCancel={onEdit}
        >
          {confirmPresContent()}
        </Dialog>
      </WebOnly>
    </>
  );
};

export default PrescriptionConfirmDialog;
